<template>
  <div
    :class="[$style.component, inline && $style.inline]"
  >
    <img
      src="../assets/images/makkis.svg"
      :class="[
        $style.animatedGuy,
        'animate__animated',
        'animate__bounceIn'
      ]"
    />
    <div ref="zoomContainerRef">
      <h1>
        <template v-if="heading">
          {{heading}}
        </template>
        <template v-else>
          <template v-if="stars === 3">
            Onnistuit hyvin!
          </template>
          <template v-else-if="stars === 2">
            Oikein hyvin meni!
          </template>
          <template v-else>
            Noh, ehkä voisit kokeilla vielä kerran
          </template>
        </template>
      </h1>
      <div
        v-if="texts.length > 0 || stars"
        :class="[$style.texts, $style.endscreenText, 'has-l-body-size']"
      >
        <div
          :class="$style.text"
          v-for="(text, index) in texts"
          :key="index"
        >
          <CheckIcon/> {{text}}
        </div>
        <div
          :class="$style.stars"
          v-if="stars"
        >
          <CheckIcon/> Ansaitsit {{stars}} {{stars === 1 ? 'tähden' : 'tähteä'}}

          <div
            v-if="stars"
            :class="$style.starRating"
          >
            <div :class="$style.stars">
              <StarIcon
                v-for="index in stars"
                :key="index"
                :class="[
                  'animate__animated',
                  'animate__bounceIn'
                ]"
                filled
              />
            </div>
          </div>
        </div>
        <div
          v-if="learningPoints && learningPoints.length > 0"
          :class="[$style.learningPoints]"
        >
        <div :class="$style.text">
          <CheckIcon/> Opit myös että:
        </div>
        <ul
          :class="[$style.learningPointsList, 'has-m-body-size']"
        >
          <li
            :class="[
              'animate__animated',
              'animate__bounceIn'
            ]"
            :style="{'animation-delay': (index * 0.25 + 1) + 's'}"
            v-for="(learning, index) in learningPoints"
            :key="index"
          >
            {{learning}}
          </li>
        </ul>
      </div>
      </div>
      <div
        v-if="time"
        :class="[
          $style.timeRating,
          'has-m-heading-size',
          'animate__animated',
          'animate__bounceIn'
        ]"
      >
        <ClockIcon :size="33" /> {{formatTime(time)}}
      </div>
      <div :class="$style.buttons">
        <button :disabled="endClicked" @click="end">OK</button>
        <button v-if="!noRetry" :disabled="endClicked" @click="$emit('retry')" class="text">Pelaa uudelleen</button>
      </div>
    </div>
  </div>
</template>

<script>
import {computed, ref} from '@vue/composition-api'

import CheckIcon from '@/components/CheckIcon'
import ClockIcon from '@/components/ClockIcon'
import StarIcon from '@/components/StarIcon'
import useZoom from '@/composition/useZoom'
import {formatTime} from '@/utils/time'

export default {
  props: {
    heading: {type: String, required: false},
    text: {
      required: false,
      validator (value) {
        if (
          typeof value !== 'undefined' &&
          typeof value !== 'string' &&
          !Array.isArray(value)
        ) return false
        return true
      },
    },
    learningPoint: {
      required: false,
      validator (value) {
        if (
          typeof value !== 'undefined' &&
          typeof value !== 'string' &&
          !Array.isArray(value)
        ) return false
        return true
      },
    },
    stars: {type: Number, required: false},
    time: {type: Number, required: false},
    noRetry: {type: Boolean, required: false},
    inline: {type: Boolean, required: false},
  },
  setup (props, context) {
    const endClicked = ref(false)
    const zoomContainerRef = ref()
    useZoom(zoomContainerRef, 1.2)

    return {
      endClicked,
      zoomContainerRef,
      end () {
        endClicked.value = true
        context.emit('end')
      },
      texts: computed(() => {
        if (typeof props.text === 'string') {
          return [props.text]
        } else if (Array.isArray(props.text)) {
          return [...props.text]
        }
        return []
      }),
      learningPoints: computed(() => {
        if (typeof props.learningPoint === 'string') {
          return [props.learningPoint]
        } else if (Array.isArray(props.learningPoint)) {
          return [...props.learningPoint]
        }
        return []
      }),
      formatTime,
    }
  },
  components: {
    CheckIcon,
    ClockIcon,
    StarIcon,
  },
}
</script>

<style module lang="scss">
.component {
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: variables.$spacing-16;
  text-align: center;

  &:not(.inline) {
    position: absolute;
    top: 0;
    left: 0;
    min-height: 100%;
  }

  > div {
    max-width: variables.$breakpoint-1024;
  }

  &#{&} h1 {
    margin-bottom: variables.$spacing-60;

    @include media.query($to: 1024) {
      font-size: 50px;
      line-height: 56px;
    }
  }
}

.animatedGuy {
  position: absolute;
  top: 25%;
  right: -50px;
  width: clamp(150px, 200px, 200px);
  z-index: 1;

  @include media.query($to: 600) {
    top: 40%;
    width: 100px;
    right: -25px;
  }
}

.buttons {
  display: flex;
  flex-direction: column;
  align-items: center;

  > * {
    margin-bottom: variables.$spacing-40;
  }
}

.texts {
  display: inline-block;
  margin-bottom: variables.$spacing-60;
  min-width: variables.$breakpoint-780;
  max-width: 100%;
  text-align: left;

  .text,
  > .stars {
    display: flex;
    align-items: flex-start;

    > *:first-child {
      margin-right: variables.$spacing-12;
      flex-shrink: 0;
      flex-grow: 0;
      flex-basis: variables.$spacing-32;
      height: variables.$line-height-l-body-mobile;

      @include media.query($from: 1024) {
        height: variables.$line-height-l-body;
      }
    }
  }

  .learningPointsList {
    margin-top: 0;
    padding-inline-start: 62px;

    li {
      margin-top: 22px;
      margin-bottom: 22px;
    }
  }

  > * + * {
    margin-top: variables.$spacing-24;
  }
}

.endscreenText {
  > div {
    @include media.query($to: 1024) {
      font-size: 28px;
      line-height: 32px;
    }
  }
}

.starRating {
  align-self: center;
  zoom: 0.4;
  .stars {
    display: flex;
    margin-left: variables.$spacing-40;

    > * {
      margin-left: variables.$spacing-40 / 2;
      margin-right: variables.$spacing-40 / 2;

      &:nth-child(1) {
        animation-delay: 0.25s;
      }
      &:nth-child(2) {
        animation-delay: 0.5s;
      }
      &:nth-child(3) {
        animation-delay: 0.75s;
      }
    }
  }
}

.timeRating {
  display: flex;
  justify-content: center;
  align-items: center;

  > * {
    margin-right: variables.$spacing-12;
  }
}

.starRating + .timeRating {
  margin-top: variables.$spacing-60;
}

.starRating + .buttons,
.timeRating + .buttons {
  margin-top: variables.$spacing-60;
}
</style>
